import {
  Component,
  EventEmitter,
  Input,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AlarmOption,
  PatientBehaviorModel,
  PatientBehaviorType,
} from '../../../core/api/models';
import { StaffApiService } from '../../../core/api/services';
import { SupervisorHelperService } from '../../supervisor.helper.service';
import { AlertService } from '../../../shared/services/alert.service';
import { Utils } from '../../../core/utils';
import {
  getAlarmOptions,
  getPatientBehaviorStatusColor,
  getPatientBehaviorStatusTooltip,
} from '../../../shared/utils/utils';
import {
  ChangeLogModalComponent,
} from '../../../shared/components/change-log-modal/change-log-modal.component';
import { SparkLogEntityType } from '../../../core/api/models';

@Component({
  selector: 'app-behavior',
  templateUrl: './behavior.component.html',
  styleUrl: '../../supervisor-main.component.css',
  standalone: false,
})
export class BehaviorComponent {
  @Input() behavior: PatientBehaviorModel | undefined;

  // Order functionality inputs from parent
  @Input() orderMode: 'alphabetical' | 'status' | 'custom' = 'alphabetical';
  @Input() isEditMode: boolean = false;

  // Selection functionality inputs from parent
  @Input() isSelecting: boolean = false;
  @Input() selectAll: boolean = false;

  constructor(
    private staffApiService: StaffApiService,
    private supervisorHelperService: SupervisorHelperService,
    private alertService: AlertService
  ) {}

  showGraph = signal(false);
  showData = signal(false);
  showBIPInfo = signal(false);

  isMastered: boolean = false;
  isClosed: boolean = false;
  _isExpanded: boolean = false;
  @Input()
  get isExpanded(): boolean {
    return !!this._isExpanded;
  }

  set isExpanded(val: boolean) {
    this._isExpanded = !!val;
    this.isExpandedChange.emit(this._isExpanded); // Emit changes
  }
  @Output() isExpandedChange = new EventEmitter<boolean>();

  // Order functionality outputs to parent
  @Output() moveUp = new EventEmitter<number>();
  @Output() moveDown = new EventEmitter<number>();
  @Output() moveToTop = new EventEmitter<number>();
  @Output() moveToBottom = new EventEmitter<number>();

  // Selection functionality
  behaviorSelected: boolean = false;
  @Output() selectBehavior = new EventEmitter<number>();

  @ViewChild('changeLogModal') changeLogModal!: ChangeLogModalComponent;
  entityTypeBehavior = SparkLogEntityType.Behavior;

  ngOnInit(): void {
    this.isMastered = this.behavior?.IsMastered ?? false;
    this.isClosed = this.behavior?.IsClosed ?? false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isSelecting']) {
      if (this.isSelecting == false) {
        this.behaviorSelected = false;
      }
    }
    if (changes['selectAll']) {
      if (this.selectAll == true) {
        this.behaviorSelected = true;
        this.selectBehavior.emit(this.behavior?.ID ?? 0);
      } else this.behaviorSelected = false;
    }
  }

  toggleCollapsed() {
    if (!this.isSelecting) {
      this.isExpanded = !this.isExpanded;
    } else {
      this.behaviorSelected = !this.behaviorSelected;
      this.selectBehavior.emit(this.behavior?.ID ?? 0);
    }
  }

  getCircleTooltip() {
    return getPatientBehaviorStatusTooltip(this.behavior);
  }

  getCircleColor() {
    return getPatientBehaviorStatusColor(this.behavior);
  }

  toggleMastery() {
    this.staffApiService
      .MarkBehaviorMastered({ patientBehaviorID: this.behavior?.ID ?? 0 })
      .subscribe(() => {
        this.supervisorHelperService.triggerUpdateSignal();
      });
  }

  toggleClosed() {
    this.staffApiService
      .MarkBehaviorClosed({ patientBehaviorID: this.behavior?.ID ?? 0 })
      .subscribe(() => {
        this.supervisorHelperService.triggerUpdateSignal();
      });
  }

  patientBehaviorTypeLabel(behaviorType: number | undefined) {
    if (!behaviorType) return 'Unspecified';
    return Utils.patientBehaviorTypeLabel(behaviorType);
  }

  editBehavior() {
    console.log('editBehavior clicked!');
    this.supervisorHelperService.addBehaviorButtonClicked(
      this.behavior ?? null
    );
  }

  deleteBehavior() {
    console.log('deleteBehavior clicked!');

    this.alertService
      .confirm(
        'Delete Behavior',
        'Are you sure you want to delete this behavior? It will be marked as deleted.'
      )
      .subscribe((result) => {
        if (result) {
          this.deleteBehaviorConfirmed();
        }
      });
  }

  archiveBehavior() {
    console.log('deleteBehavior clicked!');

    this.alertService
      .confirm(
        'Archive Behavior',
        'Are you sure you want to archive this behavior? It will be marked as archived.'
      )
      .subscribe((result) => {
        if (result) {
          this.archiveBehaviorConfirmed();
        }
      });
  }

  restoreBehavior() {
    if (this.behavior?.ID != null) {
      this.staffApiService
        .RestoreBehavior({ patientBehaviorID: this.behavior?.ID ?? 0 })
        .subscribe(() => {
          this.alertService.success('behavior restored');
          this.supervisorHelperService.triggerUpdateSignal();
        });
    }
  }

  deleteBehaviorConfirmed() {
    if (this.behavior?.ID != null) {
      this.staffApiService
        .MarkBehaviorAsDeleted({ patientBehaviorID: this.behavior.ID })
        .subscribe(
          () => {
            this.alertService.success('Behavior is now marked as deleted');
            this.supervisorHelperService.triggerUpdateSignal();
          },
          (error) => {
            console.error(error);
            this.alertService.error(
              'Failed to mark behavior as deleted - ' +
                Utils.getErrorMessageFromErrorResponse(error)
            );
          }
        );
    }
  }

  archiveBehaviorConfirmed() {
    if (this.behavior?.ID != null) {
      this.staffApiService
        .MarkBehaviorAsArchived({ patientBehaviorID: this.behavior.ID })
        .subscribe(
          () => {
            this.alertService.success('Behavior is now marked as archived');
            this.supervisorHelperService.triggerUpdateSignal();
          },
          (error) => {
            console.error(error);
            this.alertService.error(
              'Failed to mark behavior as archived - ' +
                Utils.getErrorMessageFromErrorResponse(error)
            );
          }
        );
    }
  }

  onShowGraphClicked() {
    this.showGraph.update((oldValue) => !oldValue);
  }

  onShowDataClicked() {
    this.showData.update((oldValue) => !oldValue);
  }

  onShowBIPInfoClicked() {
    this.showBIPInfo.update((oldValue) => !oldValue);
  }

  shouldDisplayIntervalField() {
    return (
      this.behavior?.BehaviorType === 3 || this.behavior?.BehaviorType === 4
    );
  }

  secondsToFormattedTime(seconds: number) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds
        .toString()
        .padStart(2, '0')}`;
    } else {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
  }

  shouldShowAlarmOptions() {
    return (
      this.behavior?.BehaviorType === PatientBehaviorType.RateTimer ||
      this.behavior?.BehaviorType === PatientBehaviorType.Interval
    );
  }

  getAlarmOption() {
    return this.behavior?.AlarmOption ?? AlarmOption.NoAlarm;
  }

  getAlarmSound() {
    return 'option ' + (this.behavior?.AlarmSoundOption ?? 1);
  }

  playAlarmSound() {
    const selectedSoundToPlay = this.behavior?.AlarmSoundOption ?? 1;
    const audio = new Audio(`assets/sounds/alarm-${selectedSoundToPlay}.mp3`);
    audio.play();
    setTimeout(() => {
      audio.pause();
    }, 5000);
  }

  getLabelForAlarmOption(alarmOption: AlarmOption) {
    return (
      getAlarmOptions().find((a) => a.value === alarmOption)?.label ?? 'none'
    );
  }

  // Order control methods
  onMoveUp() {
    if (this.behavior?.ID) {
      this.moveUp.emit(this.behavior.ID);
    }
  }

  onMoveDown() {
    if (this.behavior?.ID) {
      this.moveDown.emit(this.behavior.ID);
    }
  }

  onMoveToTop() {
    if (this.behavior?.ID) {
      this.moveToTop.emit(this.behavior.ID);
    }
  }

  onMoveToBottom() {
    if (this.behavior?.ID) {
      this.moveToBottom.emit(this.behavior.ID);
    }
  }

  isBehaviorSelected(): boolean {
    if (this.behaviorSelected === true && this.isSelecting === true)
      return true;

    return false;
  }

  showChangeLog(): void {
    if (this.changeLogModal) {
      this.changeLogModal.open();
    }
  }
}
